import { Money } from '@klear/money'
import { useMemo } from 'react'
import type { KlearAccountTransactionRead } from '@klear/treasury/klear-account-transaction-read'
import { KlearDate } from '@klear/date'
import { Timestamp } from '@klear/time'
import type { KlearFinancialAccount } from '@klear/klear-financial-account/klear-financial-account'
import {
  calculateKlearAccountDailyBalance,
  calculateKlearAccountTransactionRunningBalances
} from '@klear/treasury/daily-balance'

export type TransactionWithBalance = {
  transaction: KlearAccountTransactionRead
  balanceAfter: Money
}

export type MonthlyStatement = {
  monthKey: string
  monthName: string
  monthDate: KlearDate
  transactions: TransactionWithBalance[]
  totalInflow: Money
  totalOutflow: Money
  netFlow: Money
  transactionCount: number
  monthlyStartingBalance: Money
  monthlyEndingBalance: Money
}

function statementInstant(transaction: KlearAccountTransactionRead): Timestamp {
  return transaction.postedAt ?? transaction.createdAt
}

export const useMonthlyStatements = (
  klearFinancialAccount: KlearFinancialAccount | null | undefined,
  transactions: KlearAccountTransactionRead[] = []
): MonthlyStatement[] =>
  useMemo(() => buildMonthlyStatements(klearFinancialAccount, transactions), [klearFinancialAccount, transactions])

export function buildMonthlyStatements(
  klearFinancialAccount: KlearFinancialAccount | null | undefined,
  transactions: KlearAccountTransactionRead[]
): MonthlyStatement[] {
  if (!klearFinancialAccount?.account) {
    return []
  }

  const today = KlearDate.today()
  const accountCreatedDate =
    KlearDate.parse(Timestamp.fromEpochMs(klearFinancialAccount.createdAt).toString('YYYY-MM-DD')) ?? KlearDate.today()
  const accountCreatedMonth = accountCreatedDate.startOf('month')
  const tenthOfThisMonth = today.startOf('month').add(9, 'day')
  const lastEligibleMonth = today.isBefore(tenthOfThisMonth)
    ? today.subtract(2, 'month').startOf('month')
    : today.subtract(1, 'month').startOf('month')

  const effectiveDate = (transaction: KlearAccountTransactionRead): KlearDate =>
    KlearDate.parse(statementInstant(transaction).toString('YYYY-MM-DD')) ?? KlearDate.today()
  const effectiveMonth = (transaction: KlearAccountTransactionRead): KlearDate =>
    effectiveDate(transaction).startOf('month')

  const settledTransactions = transactions.filter((t) => t.isSettled)

  const firstTxn = settledTransactions[0]
  const earliestTransactionMonth =
    firstTxn !== undefined
      ? settledTransactions.reduce((earliest, t) => {
          const m = effectiveMonth(t)
          return m.isBefore(earliest) ? m : earliest
        }, effectiveMonth(firstTxn))
      : accountCreatedMonth
  const startMonth = accountCreatedMonth.isBefore(earliestTransactionMonth)
    ? accountCreatedMonth
    : earliestTransactionMonth

  const monthCount = Math.max(0, lastEligibleMonth.diff(startMonth, 'month') + 1)
  const daysToCoverStatements = today.diff(startMonth, 'day') + 1
  const windowDays = Math.max(daysToCoverStatements, 90)

  const dailyBalances = calculateKlearAccountDailyBalance(
    transactions,
    klearFinancialAccount.balanceCash,
    windowDays,
    'posted'
  )

  const dailyBalanceMap = new Map<string, Money>()
  dailyBalances.forEach(({ date, balance }: { date: string; balance: Money }) => {
    dailyBalanceMap.set(date, balance)
  })

  const runningBalances = calculateKlearAccountTransactionRunningBalances(
    transactions,
    klearFinancialAccount.balanceCash
  )

  const transactionsWithBalance: TransactionWithBalance[] = [...runningBalances]
    .reverse()
    .map(({ transaction, balanceAfter }) => ({ transaction, balanceAfter }))

  const transactionsByMonth = new Map<string, TransactionWithBalance[]>()

  for (let i = 0; i < monthCount; i++) {
    const monthDate = startMonth.add(i, 'month')
    const monthKey = monthDate.toString('YYYY-MM')
    transactionsByMonth.set(monthKey, [])
  }

  transactionsWithBalance.forEach((transactionWithBalance) => {
    const monthKey = effectiveMonth(transactionWithBalance.transaction).toString('YYYY-MM')
    transactionsByMonth.get(monthKey)?.push(transactionWithBalance)
  })

  const monthlySummaries: MonthlyStatement[] = []

  for (let i = 0; i < monthCount; i++) {
    const monthDate = startMonth.add(i, 'month')
    const monthKey = monthDate.toString('YYYY-MM')
    const monthTransactions = transactionsByMonth.get(monthKey) || []

    const firstDayOfMonth = monthDate.startOf('month').toString('YYYY-MM-DD')
    const lastDayOfMonth = monthDate.endOf('month').toString('YYYY-MM-DD')

    const firstTxnOfMonth = monthTransactions[0]
    const lastTxnOfMonth = monthTransactions[monthTransactions.length - 1]

    const monthlyStartingBalance =
      firstTxnOfMonth != null
        ? firstTxnOfMonth.balanceAfter.subtract(firstTxnOfMonth.transaction.amount)
        : (dailyBalanceMap.get(firstDayOfMonth) ?? Money.zero())

    const monthlyEndingBalance =
      lastTxnOfMonth != null
        ? lastTxnOfMonth.balanceAfter
        : (dailyBalanceMap.get(lastDayOfMonth) ?? monthlyStartingBalance)

    const totalInflow = monthTransactions
      .filter((tx) => tx.transaction.amount.isPositive())
      .reduce((sum, tx) => sum.add(tx.transaction.amount), Money.zero())

    const totalOutflow = monthTransactions
      .filter((tx) => tx.transaction.amount.isNegative())
      .reduce((sum, tx) => sum.subtract(tx.transaction.amount), Money.zero())

    const netFlow = totalInflow.subtract(totalOutflow)
    const transactionCount = monthTransactions.length

    monthlySummaries.push({
      monthKey,
      monthName: monthDate.toString('MMMM YYYY'),
      monthDate,
      transactions: monthTransactions,
      totalInflow,
      totalOutflow,
      netFlow,
      transactionCount,
      monthlyStartingBalance,
      monthlyEndingBalance
    })
  }

  return monthlySummaries.sort((a, b) => (a.monthDate.isAfter(b.monthDate) ? -1 : 1))
}
